import { useState, useRef, useEffect } from 'react';
import { useLatest, useMemoizedFn } from 'ahooks';
import { replaceTriggerOrInsertAt } from '../../mentionShared/mentionText';
import type { TInputSurface } from '../../mentionShared/useMentionTrigger';
import { isMentionEditorSurface } from '../../mentionEditor/types';

/** 浮窗 overlay 与搜索框 className（不动公共组件付出的耦合代价，需随其同步） */
const POPOVER_OVERLAY = '.dba-new-chart-popover-overlay';
const SEARCH_INPUT = '.dbc-region-select-search-input';

export type TChartMentionOpenReason = 'button' | 'hash';

export interface IUseChartMentionHostOptions {
  inputValue: string;
  setInputValue: (value: string) => void;
  textareaRef: React.RefObject<TInputSurface | null>;
}

export interface IChartMentionHostProps {
  chartDropdownOpen: boolean;
  onChartDropdownOpenChange: (open: boolean) => void;
  chartMentionQuery: string;
  chartMentionOpenReason: TChartMentionOpenReason;
}

export interface IUseChartMentionHostResult {
  /** 返回 true 表示本 hook 已 setInputValue，调用方应直接 return */
  wrapTextareaChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => boolean;
  chartMentionHostProps: IChartMentionHostProps;
  isHashModeOpen: boolean;
  /** 选中 chart 后清理：只删 `#` + query，不再插入 inline label（chart 走顶部 Tag，不进正文） */
  cleanupHashText: () => void;
  handleTextareaFocus: () => void;
}

const findSearchInput = (): HTMLInputElement | null =>
  document.querySelector(`${POPOVER_OVERLAY} ${SEARCH_INPUT}`);

/** native value setter + dispatch input event，让 NewRegionSelect 自身 setSearch 接收 */
const writeSearchValue = (input: HTMLInputElement, query: string) => {
  if (input.value === query) return;
  const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
  setter?.call(input, query);
  input.dispatchEvent(new Event('input', { bubbles: true }));
};

/** contentEditable 首字符 # 时 selectionStart 可能为 0 */
const resolveHashCursor = (value: string, cursorPos: number): number =>
  cursorPos <= 0 && value.charAt(0) === '#' ? 1 : cursorPos;

/**
 * ChartPopover 与 textarea 的衔接：`#` 唤起、query 同步、cleanup。
 * 对齐 SkillMention 3 场景：① 新输入 # ② 删除后光标贴齐 # ③ focus 时贴齐 #
 */
export const useChartMentionHost = ({
  inputValue,
  setInputValue,
  textareaRef,
}: IUseChartMentionHostOptions): IUseChartMentionHostResult => {
  const [chartDropdownOpen, setChartDropdownOpen] = useState(false);
  const [chartMentionOpenReason, setChartMentionOpenReason] =
    useState<TChartMentionOpenReason>('button');
  const [chartMentionQuery, setChartMentionQuery] = useState('');
  const hashIndexRef = useRef<number>(-1);

  const openLatest = useLatest(chartDropdownOpen);
  const inputLatest = useLatest(inputValue);
  const isHashModeOpen = chartDropdownOpen && chartMentionOpenReason === 'hash';

  /** 光标紧跟 # 时打开浮窗（场景 ①②③ 共用） */
  const tryOpenHashDropdown = useMemoizedFn((value: string, cursorPos: number): boolean => {
    const pos = resolveHashCursor(value, cursorPos);
    if (openLatest.current || pos === 0 || !value) return false;
    if (value.charAt(pos - 1) !== '#') return false;
    hashIndexRef.current = pos - 1;
    setChartMentionOpenReason('hash');
    setChartMentionQuery('');
    setChartDropdownOpen(true);
    return true;
  });

  const wrapTextareaChange = useMemoizedFn(
    (e: React.ChangeEvent<HTMLTextAreaElement>): boolean => {
      const newValue = e.target.value;
      const cursorPos = e.target.selectionStart || 0;

      // 浮窗已开：同步 query / 检测 # 被删
      if (openLatest.current && hashIndexRef.current >= 0) {
        setInputValue(newValue);
        const hashIdx = hashIndexRef.current;
        if (hashIdx < newValue.length && newValue.charAt(hashIdx) === '#') {
          const afterHash = newValue.substring(hashIdx + 1);
          const match = afterHash.match(/^([^\s]*)/);
          setChartMentionQuery(match ? match[1] : '');
        } else {
          setChartDropdownOpen(false);
          hashIndexRef.current = -1;
          setChartMentionQuery('');
        }
        return true;
      }

      // 场景 ① / ②：光标贴齐 # → 打开
      if (tryOpenHashDropdown(newValue, cursorPos)) {
        setInputValue(newValue);
        return true;
      }

      return false;
    },
  );

  const onChartDropdownOpenChange = useMemoizedFn((open: boolean) => {
    if (open) {
      setChartMentionOpenReason('button');
    } else {
      setChartMentionQuery('');
      // 不能在这里清 hashIndexRef：cleanupHashText 同帧需读，统一在其内部清零
    }
    setChartDropdownOpen(open);
  });

  /** 选中 chart 后：仅删 `#` + query，chart 数据走顶部 Tag，不写入正文 */
  const cleanupHashText = useMemoizedFn(() => {
    if (hashIndexRef.current < 0) return;

    const surface = textareaRef.current;
    const hashIdx = hashIndexRef.current;

    if (isMentionEditorSurface(surface)) {
      surface.insertMentionAtTrigger(hashIdx, '', 'skill', 'hash-cleanup', '#');
      hashIndexRef.current = -1;
      setChartMentionQuery('');
      return;
    }

    const current = inputLatest.current || '';
    const textarea = surface as HTMLTextAreaElement | null;
    const { nextValue, cursor } = replaceTriggerOrInsertAt(current, '', {
      triggerIndex: hashIdx,
      selectionStart: textarea?.selectionStart ?? current.length,
      selectionEnd: textarea?.selectionEnd ?? current.length,
    });
    setInputValue(nextValue);
    hashIndexRef.current = -1;
    setChartMentionQuery('');
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el || isMentionEditorSurface(el)) return;
      el.selectionStart = cursor;
      el.selectionEnd = cursor;
    });
  });

  /** 场景 ③：focus 时光标贴齐已有 # → 重新打开 */
  const handleTextareaFocus = useMemoizedFn(() => {
    setTimeout(() => {
      const surface = textareaRef.current;
      if (!surface) return;
      const cursorPos = isMentionEditorSurface(surface)
        ? surface.selectionStart
        : surface.selectionStart ?? 0;
      tryOpenHashDropdown(inputLatest.current || '', cursorPos);
    }, 0);
  });

  /**
   * # 模式 DOM hack（外部接管，公共组件零侵入）：
   * - inert 阻止 NewRegionSelect 内部 .focus() 抢 textarea 焦点
   * - native value setter + dispatch input event 把 mentionQuery 同步给其自身 setSearch
   * - Tea Dropdown portal 渲染，rAF 兜底重试浮窗 mount 时序
   */
  useEffect(() => {
    if (!isHashModeOpen) return;
    let searchInput: HTMLInputElement | null = null;
    let rafId = 0;
    const apply = () => {
      searchInput = findSearchInput();
      if (!searchInput) {
        rafId = window.requestAnimationFrame(apply);
        return;
      }
      (searchInput as any).inert = true;
      const surface = textareaRef.current;
      if (surface) {
        const caret = hashIndexRef.current >= 0 ? hashIndexRef.current + 1 : 1;
        surface.focus();
        if (isMentionEditorSurface(surface)) {
          surface.setSelectionRange(caret, caret);
        } else {
          const el = surface as HTMLTextAreaElement;
          el.selectionStart = caret;
          el.selectionEnd = caret;
        }
      }
      writeSearchValue(searchInput, chartMentionQuery);
    };
    apply();
    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      if (searchInput) (searchInput as any).inert = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHashModeOpen]);

  /** query 变化时持续同步给搜索框（已 inert 不会抢焦点） */
  useEffect(() => {
    if (!isHashModeOpen) return;
    const searchInput = findSearchInput();
    if (searchInput) writeSearchValue(searchInput, chartMentionQuery);
  }, [chartMentionQuery, isHashModeOpen]);

  return {
    wrapTextareaChange,
    chartMentionHostProps: {
      chartDropdownOpen,
      onChartDropdownOpenChange,
      chartMentionQuery,
      chartMentionOpenReason,
    },
    isHashModeOpen,
    cleanupHashText,
    handleTextareaFocus,
  };
};

export default useChartMentionHost;
